import type {
  AdditionalValues,
  nullable,
  IHealth,
  IFeatureUsageAndHealthLogger,
  IKpiUsageOptionalProperties,
  IKpiFailureOptionalProperties,
  IQosErrorOptionalProperties,
  OptionalVetoType,
  IKpiSuccessOptionalProperties,
  FeatureUsageAndHealthConfig
} from "../generated/healthSmall.gen";
import { KpiType } from "../generated/healthSmall.gen";
import type { TimeBasedEventBlocker } from "./TimeBasedEventBlocker";
import { ONE_DAY_IN_MS, ONE_MINUTE_IN_MS } from "./TimeBasedEventBlocker";

/**
 * @see {IFeatureUsageAndHealthLogger}
 */
export class FeatureUsageAndHealthLogger
  implements IFeatureUsageAndHealthLogger
{
  /** Name of extended field/column that will contain the feature's name and is appended to every event. */
  private static FEATURE_NAME_COLUMN_KEY = "FeatureName";

  /** Name of extended field/column that will contain the veto pillar/area name and is appended to every event (except for user actions and activites). */
  private static AREA_NAME_COLUMN_KEY = "AreaName";

  /** Name of extended field/column that will contain the aggregated event hit count (only for throttled events) and is appended to every event (except for user actions and activites). */
  private static AGGREGATED_COUNT_COLUMN_KEY = "AggCount";

  /** Name of extended field/column that will contain the aggregated event durations (only for throttled events) and is appended to every event (except for user actions and activites). */
  private static AGGREGATED_DURATION_COLUMN_KEY = "AggMs";

  /** Name of extended field/column that is by default set to true so the events are not sent to Aria, and are only sent to the Word Online kusto cluster and is appended to every event (except for user actions and activites). */
  private static SKIP_ARIA_COLUMN_KEY = "SkipAria";

  /** Name of extended field/column used to configure type of the pivot, pivots are used to split KPIs into multiple sub-kpis with independent aggregation. */
  private static PIVOT_TYPE_COLUMN_KEY = "PivotType";

  /** Name of extended field/column used to configure value of the pivot, pivots are used to split KPIs into multiple sub-kpis with independent aggregation. */
  private static PIVOT_VALUE_COLUMN_KEY = "PivotValue";

  /** List of QoS pillars that we can register in ensureFeatureDenominatorLoggedAndVetoPillarRegistered (Save, RealTimeCoauth, and ContentCreation are excluded since sessions will already be centrally added to these pillars). */
  private static QOS_PILLARS_LIST = [
    "Interruptions",
    "Open",
    "NotebookNavigation",
    "Fidelity",
    "AsyncCollab",
    "Present",
    "ContentConsumption",
    "bSqmCompatibility",
    "Boot"
  ];

  /** Constants that are appended to KPI names to differentiate KPI events for throttling. */
  private static USAGE_KPI = ".Usage";
  private static SUCCESS_KPI = ".Success";
  private static FAILURE_KPI = ".Failure.";

  /**
   * Initializes the FeatureUsageAndHealthLogger with the provided configuration.
   *
   * @param healthLogger  Instance of Health 2.0 API to log KPI and QOS events -- e.g. `Health.instance`.
   * @param featureConfig  Configuration that specifies properties like feature's name, veto pillar, and so on, that are used by this class.
   */
  constructor(
    private readonly healthLogger: IHealth,
    private readonly featureConfig: FeatureUsageAndHealthConfig,
    private readonly kpiUsageEventBlocker: TimeBasedEventBlocker<string>,
    private readonly kpiFailureEventBlocker: TimeBasedEventBlocker<string>,
    private readonly kpiSuccessEventBlocker: TimeBasedEventBlocker<string>
  ) {}

  /**
   * Logs a usage KPI.
   *
   * Automatically logs feature's all-up usage KPI and registers its corresponding veto pillar, if this hasn't been done before.
   *
   * @param kpiName  Name of the KPI.
   * @param kpiType  Type of KPI (e.g. failure-based for implicit success).
   * @param optionalProperties  Optional properties to log with KPI.
   * @param blockFutureEmissionDurationInMs  Optional duration for which to block future emissions of this event.
   *
   * @returns Whether the event was processed. An event is skipped if it was blocked in a previous call and the block duration has not expired.
   */
  public logKpiUsage(
    kpiName: string | nullable,
    kpiType: KpiType,
    optionalProperties?: IKpiUsageOptionalProperties | nullable,
    blockFutureEmissionDurationInMs?: number | nullable
  ): boolean {
    // For event throttling, we need to differentiate a KPI's usage events from its success/error events
    const kpiUsageKey = this.getKpiKey(
      kpiName + FeatureUsageAndHealthLogger.USAGE_KPI,
      optionalProperties?.extendedProperties
    );

    const recordKpiCall = () =>
      this.recordKpiUsage(kpiName, kpiType, optionalProperties, kpiUsageKey);
    if (this.kpiUsageEventBlocker.isEventBlocked(kpiUsageKey, recordKpiCall)) {
      return false;
    }

    this.ensureFeatureDenominatorLoggedAndVetoPillarRegistered(
      optionalProperties
    );
    recordKpiCall();

    if (blockFutureEmissionDurationInMs !== 0) {
      // By default, throttle every event for 5 minutes unless otherwise specified
      const blockDuration =
        blockFutureEmissionDurationInMs ?? ONE_MINUTE_IN_MS * 5;
      // Block KPI usage for duration
      this.kpiUsageEventBlocker.blockEvent(kpiUsageKey, blockDuration);
    }

    return true;
  }

  /**
   * Logs a failure for the provided usage KPI.
   *
   * Automatically logs feature's all-up usage KPI and registers its corresponding veto pillar, if this hasn't been done before.
   *
   * @param kpiName  Name of the usage KPI associated with this error.
   * @param errorName  Name of the error.
   * @param optionalProperties  Optional properties to log with KPI failure.
   * @param optionalVeto  Optional veto to log alongside KPI failure.
   * @param blockFutureEmissionDurationInMs  Optional duration for which to block future emissions of this event.
   *
   * @returns Whether the event was processed. An event is skipped if it was blocked in a previous call and the block duration has not expired.
   */
  public logKpiFailure(
    kpiName: string | nullable,
    errorName: string | nullable,
    optionalProperties?: IKpiFailureOptionalProperties | nullable,
    optionalVeto?: OptionalVetoType | nullable,
    blockFutureEmissionDurationInMs?: number | nullable
  ): boolean {
    // For event throttling, we need to differentiate between different errors for the same KPI failure
    const kpiFailureKey = this.getKpiKey(
      kpiName + FeatureUsageAndHealthLogger.FAILURE_KPI + errorName,
      optionalProperties?.additionalValues?.extendedProperties
    );

    if (typeof optionalProperties?.additionalValues?.durationMs === "number") {
      this.kpiFailureEventBlocker.aggregateEventDuration(
        kpiFailureKey,
        optionalProperties.additionalValues.durationMs
      );
    }

    const recordKpiCall = () =>
      this.recordKpiFailure(
        kpiName,
        errorName,
        optionalProperties,
        kpiFailureKey
      );

    if (
      this.kpiFailureEventBlocker.isEventBlocked(kpiFailureKey, recordKpiCall)
    ) {
      return false;
    }

    this.ensureFeatureDenominatorLoggedAndVetoPillarRegistered();
    recordKpiCall();

    if (optionalVeto) {
      // Use KPI error name if no veto name override was provided
      this.recordQosError(
        optionalVeto.errorNameOverride ?? errorName,
        optionalVeto.name,
        optionalVeto
      );
    }

    if (blockFutureEmissionDurationInMs !== 0) {
      // By default, throttle every event for 5 minutes unless otherwise specified
      const blockDuration =
        blockFutureEmissionDurationInMs ?? ONE_MINUTE_IN_MS * 5;
      // Block KPI failure for duration
      this.kpiFailureEventBlocker.blockEvent(kpiFailureKey, blockDuration);
    }

    return true;
  }

  /**
   * Logs a success for the provided usage KPI.
   *
   * Automatically logs feature's all-up usage KPI and registers its corresponding veto pillar, if this hasn't been done before.
   *
   * @param kpiName  Name of the KPI.
   * @param optionalProperties  Optional properties to log with KPI.
   * @param blockFutureEmissionDurationInMs  Optional duration for which to block future emissions of this event.
   *
   * @returns Whether the event was processed. An event is skipped if it was blocked in a previous call and the block duration has not expired.
   */
  public logKpiSuccess(
    kpiName: string | nullable,
    optionalProperties?: IKpiSuccessOptionalProperties | nullable,
    blockFutureEmissionDurationInMs?: number | nullable
  ): boolean {
    // For event throttling, we need to differentiate a KPI's success events from its usage/error events
    const kpiSuccessKey = this.getKpiKey(
      kpiName + FeatureUsageAndHealthLogger.SUCCESS_KPI,
      optionalProperties?.additionalValues?.extendedProperties
    );

    if (typeof optionalProperties?.additionalValues?.durationMs === "number") {
      this.kpiSuccessEventBlocker.aggregateEventDuration(
        kpiSuccessKey,
        optionalProperties.additionalValues.durationMs
      );
    }

    const recordKpiCall = () =>
      this.recordKpiSuccess(kpiName, optionalProperties, kpiSuccessKey);

    if (
      this.kpiSuccessEventBlocker.isEventBlocked(kpiSuccessKey, recordKpiCall)
    ) {
      return false;
    }

    this.ensureFeatureDenominatorLoggedAndVetoPillarRegistered();
    recordKpiCall();

    if (blockFutureEmissionDurationInMs !== 0) {
      // By default, throttle every event for 5 minutes unless otherwise specified
      const blockDuration =
        blockFutureEmissionDurationInMs ?? ONE_MINUTE_IN_MS * 5;
      // Block KPI success for duration
      this.kpiSuccessEventBlocker.blockEvent(kpiSuccessKey, blockDuration);
    }

    return true;
  }

  /**
   * Ensures that this feature's top-level usage KPI (denominator) was logged and its veto pillar registered.
   *
   * We register the denominator/veto pillar max once per day (not once per session because sessions can last several days and most dashboards,
   * including ASHA, use "daily" aggregations).
   *
   * @param optionalProperties Optional properties to log with usage KPI
   */
  public ensureFeatureDenominatorLoggedAndVetoPillarRegistered(
    optionalProperties?: IKpiUsageOptionalProperties | nullable
  ): void {
    const kpiName = this.featureConfig.topLevelKpiName;
    const kpiKey = this.getKpiKey(
      this.featureConfig.topLevelKpiName,
      optionalProperties?.extendedProperties
    );

    // Do not register flush callback, because once the key expires, we only want to log it again, if we hit the KPI again
    if (this.kpiUsageEventBlocker.isEventBlocked(kpiKey ?? "")) {
      return;
    }

    // Register top-level feature usage event so it can be used as a denominator for user/health metrics
    this.recordKpiUsage(kpiName, KpiType.FailureBased, optionalProperties);

    // Register feature's veto pillar if valid
    const vetoPillar =
      this.featureConfig.vetoPillar ?? this.featureConfig.areaName;
    if (
      vetoPillar &&
      FeatureUsageAndHealthLogger.QOS_PILLARS_LIST.includes(vetoPillar)
    ) {
      this.healthLogger.addQosPillar(vetoPillar);
    }

    // Block event for the next 24 hours (we only need it once per 24 hours per session)
    this.kpiUsageEventBlocker.blockEvent(kpiKey ?? "", ONE_DAY_IN_MS);
  }

  /**
   *
   * Internal helper functions.
   *
   */

  private recordKpiUsage(
    name: string | nullable,
    kpiType: KpiType,
    optionalProperties: nullable | IKpiUsageOptionalProperties = {},
    kpiUsageKey?: string
  ): void {
    const { extendedProperties, isForAlertableKpi } =
      optionalProperties || ({} as IKpiUsageOptionalProperties);

    // Append feature name, area name, and aggregated hit count (if applicable) to KPI usage event
    const extendedPropertiesWithMoreFields =
      this.createExtendedPropertiesWithMoreFields(
        extendedProperties,
        kpiUsageKey
      );

    if (isForAlertableKpi) {
      this.healthLogger.recordKpiUsageForAlertableKpi(
        name,
        this.featureConfig.crewAlias,
        extendedPropertiesWithMoreFields
      );
    } else {
      this.healthLogger.recordKpiUsage(
        name,
        kpiType,
        this.featureConfig.crewAlias,
        extendedPropertiesWithMoreFields
      );
    }
  }

  private getKpiKey(
    defaultKpiKey: string | nullable,
    extendedProperties: Map<string | nullable, string | nullable> | nullable
  ): string {
    const areaName: string = this.getKpiAreaName(extendedProperties);
    const featureName: string = this.getKpiFeatureName(extendedProperties);
    const pivot: string = this.getKpiPivot(extendedProperties);

    return `${areaName}.${featureName}.${defaultKpiKey ?? ""}${
      pivot && pivot.length !== 0 ? "." + pivot : ""
    }`;
  }

  private getKpiAreaName(
    extendedProperties: Map<string | nullable, string | nullable> | nullable
  ): string {
    return (
      extendedProperties?.get(
        FeatureUsageAndHealthLogger.AREA_NAME_COLUMN_KEY
      ) ??
      this.featureConfig.areaName ??
      this.featureConfig.vetoPillar ??
      ""
    );
  }

  private getKpiFeatureName(
    extendedProperties: Map<string | nullable, string | nullable> | nullable
  ): string {
    return (
      extendedProperties?.get(
        FeatureUsageAndHealthLogger.FEATURE_NAME_COLUMN_KEY
      ) ??
      this.featureConfig.featureName ??
      ""
    );
  }

  private getKpiPivot(
    extendedProperties: Map<string | nullable, string | nullable> | nullable
  ): string {
    const pivotType = extendedProperties?.get(
      FeatureUsageAndHealthLogger.PIVOT_TYPE_COLUMN_KEY
    );
    const pivotValue = extendedProperties?.get(
      FeatureUsageAndHealthLogger.PIVOT_VALUE_COLUMN_KEY
    );

    return !pivotType ||
      pivotType.length === 0 ||
      !pivotValue ||
      pivotValue.length === 0
      ? ""
      : `${pivotType}=${pivotValue}`;
  }

  private recordKpiFailure(
    kpiName: string | nullable,
    errorName: string | nullable,
    optionalProperties: nullable | IKpiFailureOptionalProperties = {},
    kpiFailureKey?: string
  ): void {
    const { isIntentional, isInternal, additionalValues, hasImpliedUsage } =
      optionalProperties || ({} as IKpiFailureOptionalProperties);

    // Append feature name, area name, and aggregated hit count (if applicable) to KPI failure event
    const additionalValuesWithMoreFields =
      this.createAdditionalValuesWithMoreFields(
        additionalValues,
        kpiFailureKey
      );

    // Default is to use the implied function unless hasImpliedUsage is explicitly set to false
    if (hasImpliedUsage === false) {
      this.healthLogger.recordKpiFailure(
        kpiName,
        errorName,
        isIntentional,
        isInternal,
        additionalValuesWithMoreFields
      );
    } else {
      this.healthLogger.recordKpiImpliedFailure(
        kpiName,
        errorName,
        isIntentional,
        isInternal,
        this.featureConfig.crewAlias,
        additionalValuesWithMoreFields
      );
    }
  }

  private recordKpiSuccess(
    kpiName: string | nullable,
    optionalProperties: nullable | IKpiSuccessOptionalProperties = {},
    kpiSuccessKey?: string
  ): void {
    const { additionalValues, hasImpliedUsage } =
      optionalProperties || ({} as IKpiSuccessOptionalProperties);

    // Append feature name, area name, and aggregated hit count (if applicable) to KPI success event
    const additionalValuesWithMoreFields =
      this.createAdditionalValuesWithMoreFields(
        additionalValues,
        kpiSuccessKey
      );

    // Default is to use the implied function unless hasImpliedUsage is explicitly set to false
    if (hasImpliedUsage === false) {
      this.healthLogger.recordKpiSuccess(
        kpiName,
        additionalValuesWithMoreFields
      );
    } else {
      this.healthLogger.recordKpiImpliedSuccess(
        kpiName,
        this.featureConfig.crewAlias,
        additionalValuesWithMoreFields
      );
    }
  }

  private recordQosError(
    errorName: string | nullable,
    vetoName: string | nullable,
    optionalProperties: IQosErrorOptionalProperties = {}
  ): void {
    const {
      isIntentional,
      isInternal,
      alertOnPillar,
      isSessionEndingError,
      additionalValues
    } = optionalProperties;

    // Append feature name and area name to QOS error event
    const additionalValuesWithFeatureAndAreaName =
      this.createAdditionalValuesWithMoreFields(additionalValues);

    this.healthLogger.recordQosError(
      errorName,
      [vetoName],
      isIntentional,
      isInternal,
      alertOnPillar,
      isSessionEndingError,
      additionalValuesWithFeatureAndAreaName
    );
  }

  /**
   * Creates an ExtendedProperties (Map) instance that contains the feature name, area name, and
   * aggregated event hit count for throttled events in separate data fields.
   */
  private createExtendedPropertiesWithMoreFields(
    extendedPropertiesToMerge?:
      | Map<string | nullable, string | nullable>
      | nullable,
    kpiKey?: string
  ): Map<string, string> {
    const extendedPropertiesWithMoreFields = new Map<string, string>(
      extendedPropertiesToMerge || new Map()
    );

    if (
      !extendedPropertiesWithMoreFields.get(
        FeatureUsageAndHealthLogger.FEATURE_NAME_COLUMN_KEY
      )
    ) {
      extendedPropertiesWithMoreFields.set(
        FeatureUsageAndHealthLogger.FEATURE_NAME_COLUMN_KEY,
        this.getKpiFeatureName(extendedPropertiesToMerge)
      );
    }

    extendedPropertiesWithMoreFields.set(
      FeatureUsageAndHealthLogger.AREA_NAME_COLUMN_KEY,
      this.getKpiAreaName(extendedPropertiesWithMoreFields)
    );

    // By default, skip sending events to Aria unless user has explicitly set SkipAria in extendedProperties
    if (
      !extendedPropertiesWithMoreFields.has(
        FeatureUsageAndHealthLogger.SKIP_ARIA_COLUMN_KEY
      ) &&
      !extendedPropertiesWithMoreFields.has("skipAria")
    ) {
      extendedPropertiesWithMoreFields.set(
        FeatureUsageAndHealthLogger.SKIP_ARIA_COLUMN_KEY,
        "true"
      );
    }

    // Do not emit aggregated counts if this flag is set to true
    if (!this.featureConfig.doNotEmitAggregatedCount) {
      // If the event is throttled, add the aggregated hit count to extended properties during logging
      if (kpiKey) {
        const eventBlocker = this.getEventBlocker(kpiKey);
        if (eventBlocker) {
          const eventCount = eventBlocker.getEventHitCount(kpiKey);
          if (eventCount) {
            extendedPropertiesWithMoreFields.set(
              FeatureUsageAndHealthLogger.AGGREGATED_COUNT_COLUMN_KEY,
              eventCount.toString()
            );

            const eventDurations = eventBlocker.getEventDurations(kpiKey);
            if (eventCount > 1 && eventDurations) {
              extendedPropertiesWithMoreFields.set(
                FeatureUsageAndHealthLogger.AGGREGATED_DURATION_COLUMN_KEY,
                eventDurations.map((v) => Math.round(v * 100) / 100).toString() // Round to 2 decimal places
              );
            }
          }
        }
      }
    }

    return extendedPropertiesWithMoreFields;
  }

  /**
   * Creates an AdditionalValues instance that contains the feature name, area name, and
   * aggregated event hit count for throttled events in separate data fields.
   */
  private createAdditionalValuesWithMoreFields(
    additionalValuesToMerge?: AdditionalValues | nullable,
    kpiKey?: string
  ): AdditionalValues {
    let additionalValuesWithMoreFields: AdditionalValues = {};
    additionalValuesWithMoreFields = { ...additionalValuesToMerge };

    additionalValuesWithMoreFields.extendedProperties =
      this.createExtendedPropertiesWithMoreFields(
        additionalValuesToMerge?.extendedProperties,
        kpiKey
      );

    return additionalValuesWithMoreFields;
  }

  /**
   * Given a kpiKey, retrieve the corresponding TimeBasedEventBlocker instance.
   *
   * @param kpiKey  Unique identifier of event.
   */
  private getEventBlocker(
    kpiKey: string
  ): TimeBasedEventBlocker<string> | undefined {
    if (kpiKey.includes(FeatureUsageAndHealthLogger.USAGE_KPI)) {
      return this.kpiUsageEventBlocker;
    } else if (kpiKey.includes(FeatureUsageAndHealthLogger.FAILURE_KPI)) {
      return this.kpiFailureEventBlocker;
    } else if (kpiKey.includes(FeatureUsageAndHealthLogger.SUCCESS_KPI)) {
      return this.kpiSuccessEventBlocker;
    }

    return undefined;
  }
}
